window.ThemeUtils = window.ThemeUtils || {};

/**
 * Formatea un precio en centavos a formato de moneda
 * @param {number} cents - Precio en centavos
 * @param {string} currency - Código de moneda (default: 'COP')
 * @param {string} locale - Locale (default: 'es-CO')
 * @returns {string} Precio formateado
 */
ThemeUtils.formatPrice = function (price) {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0
  }).format(price);
};

/**
 * Formatea centavos usando el shop.money_format de Shopify (mismo resultado que
 * el filtro Liquid `| money`). Fuente única — antes estaba duplicada en
 * main-search, main-product, main-wishlist y search-bar.
 * @param {number} cents - Precio en centavos
 * @param {string} moneyFormat - shop.money_format (ej. "${{amount}}"), pasado desde Liquid vía data-attr
 * @returns {string} Precio formateado
 */
ThemeUtils.formatMoney = function (cents, moneyFormat) {
  moneyFormat = moneyFormat || '${{amount}}';
  var value = (parseInt(cents, 10) || 0) / 100;
  var placeholderRe = /\{\{\s*(\w+)\s*\}\}/;
  var match = moneyFormat.match(placeholderRe);
  var placeholder = match ? match[1] : 'amount';
  /* El placeholder solo decide los DECIMALES; los separadores los pone el
     locale del store (Shopify formatea con su locale, no con el placeholder).
     Tienda colombiana → es-CO = punto de miles. Igual que el filtro `| money`. */
  var decimals = placeholder.indexOf('no_decimals') !== -1 ? 0 : 2;
  var formatted = new Intl.NumberFormat('es-CO', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(value);
  return moneyFormat.replace(placeholderRe, formatted).replace(/<[^>]*>/g, '');
};

/**
 * Resalta términos de búsqueda en un texto
 * @param {string} text - Texto donde buscar
 * @param {string} query - Término a resaltar
 * @returns {string} HTML con términos resaltados en <mark>
 */
ThemeUtils.highlightMatch = function (text, query) {
  const escapeRegex = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escapeRegex(query)})`, 'gi');
  return text.replace(regex, '<mark>$1</mark>');
};

/**
 * Debounce: Retrasa la ejecución de una función
 * @param {Function} func - Función a ejecutar
 * @param {number} wait - Milisegundos de espera
 * @returns {Function} Función debounced
 */
ThemeUtils.debounce = function(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Sanitiza HTML para prevenir XSS
 * @param {string} str - String a sanitizar
 * @returns {string} String sanitizado
 */
ThemeUtils.sanitizeHTML = function(str) {
  const temp = document.createElement('div');
  temp.textContent = str;
  return temp.innerHTML;
};

/**
 * ORDENA RESULTADOS POR RELEVANCIA
 * Sistema de puntuación para priorizar resultados más relevantes
 */
ThemeUtils.sortByRelevance = function(items, query) {
  const queryLower = query.toLowerCase().trim();
  
  const itemsWithScore = items.map(item => {
    const title = item.title.toLowerCase();
    let score = 0;
    
    // 1. COINCIDENCIA EXACTA (más importante)
    if (title === queryLower) {
      score += 1000;
    }
    // 2. EMPIEZA CON EL TÉRMINO
    else if (title.startsWith(queryLower)) {
      score += 500;
    }
    // 3. EMPIEZA CON EL TÉRMINO DESPUÉS DE UN ESPACIO
    else if (title.includes(' ' + queryLower)) {
      score += 400;
    }
    // 4. PALABRA COMPLETA EN MEDIO
    else if (ThemeUtils.containsWholeWord(title, queryLower)) {
      score += 100;
    }
    // 5. CONTIENE EL TÉRMINO (parcialmente)
    else if (title.includes(queryLower)) {
      score += 50;
      const position = title.indexOf(queryLower);
      const proximityBonus = Math.max(0, 20 - position);
      score += proximityBonus;
    }
    
    // BONUS: Producto disponible
    if (item.available !== false) {
      score += 10;
    }
    
    // PENALTY: Título más largo es menos relevante
    const lengthPenalty = Math.min(title.length, 50) / 2;
    score -= lengthPenalty;
    
    return { ...item, relevanceScore: score };
  });
  
  // Ordenar por score (mayor a menor)
  itemsWithScore.sort((a, b) => b.relevanceScore - a.relevanceScore);
  
  return itemsWithScore;
};

/**
 * VERIFICA SI CONTIENE PALABRA COMPLETA
 * Evita falsos positivos como "guitarrón" cuando buscan "guitarra"
 */
ThemeUtils.containsWholeWord = function(text, word) {
  const escapeRegex = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp('\\b' + escapeRegex(word) + '\\b', 'i');
  return regex.test(text);
};

/**
 * FOCUS TRAP para drawers/modales (WCAG 2.4.3)
 * Devuelve función release() que quita el trap y devuelve el foco
 * al elemento que lo tenía antes de abrir.
 */
ThemeUtils.trapFocus = function(container, options) {
  options = options || {};
  var previouslyFocused = document.activeElement;
  var selector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function getFocusable() {
    return Array.prototype.filter.call(container.querySelectorAll(selector), function(el) {
      return el.offsetParent !== null;
    });
  }

  function onKeydown(e) {
    if (e.key === 'Escape' && options.onEscape) {
      options.onEscape();
      return;
    }
    if (e.key !== 'Tab') return;
    var focusable = getFocusable();
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  document.addEventListener('keydown', onKeydown);
  var initial = getFocusable()[0];
  if (initial) initial.focus();

  return function release() {
    document.removeEventListener('keydown', onKeydown);
    if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
      previouslyFocused.focus();
    }
  };
};

/**
 * ANUNCIA un mensaje a lectores de pantalla via la región
 * aria-live global (#sr-announcer en theme.liquid).
 */
ThemeUtils.announce = function(message) {
  var region = document.getElementById('sr-announcer');
  if (!region) return;
  region.textContent = '';
  setTimeout(function() { region.textContent = message; }, 50);
};

/**
 * Slider doble de rango (filtro de precio en búsqueda y colección).
 * Construye el widget dentro de `root` y dispara onCommit(min, max) al
 * soltar un thumb. Los valores son unidades del dominio del caller
 * (el caller decide formato con opts.format).
 */
ThemeUtils.rangeSlider = function(root, opts) {
  var min = Number(opts.min);
  var max = Number(opts.max);
  if (!(max > min)) return null;
  /* Paso fino (milésima del dominio, no centésima). El <input type=range>
     redondea al múltiplo más cercano, así que con 100 pasos sobre un catálogo
     de $24.990.000 cada paso valía $249.900 y el pulgar se veía descolocado
     respecto al valor real escrito. */
  var step = opts.step || Math.max(1, Math.round((max - min) / 1000));
  var vMin = opts.valueMin != null && opts.valueMin !== '' ? Number(opts.valueMin) : min;
  var vMax = opts.valueMax != null && opts.valueMax !== '' ? Number(opts.valueMax) : max;
  var fmt = opts.format || String;

  root.classList.add('range-slider');
  root.innerHTML =
    '<div class="range-slider__track"><div class="range-slider__fill" data-rs-fill></div>'
    + '<input type="range" data-rs-input-min aria-label="' + (opts.labelMin || 'Min') + '">'
    + '<input type="range" data-rs-input-max aria-label="' + (opts.labelMax || 'Max') + '">'
    + '</div>'
    + '<div class="range-slider__fields">'
    + '<label class="range-slider__field"><span>' + (opts.labelMin || 'Mín') + '</span>'
    + '<input type="text" inputmode="numeric" data-rs-num-min aria-label="' + (opts.labelMin || 'Min') + '"></label>'
    + '<label class="range-slider__field"><span>' + (opts.labelMax || 'Máx') + '</span>'
    + '<input type="text" inputmode="numeric" data-rs-num-max aria-label="' + (opts.labelMax || 'Max') + '"></label>'
    + '</div>';

  var iMin = root.querySelector('[data-rs-input-min]');
  var iMax = root.querySelector('[data-rs-input-max]');
  [iMin, iMax].forEach(function(i) { i.min = min; i.max = max; i.step = step; });
  iMin.value = Math.max(min, Math.min(vMin, max));
  iMax.value = Math.max(min, Math.min(vMax, max));

  var nMin = root.querySelector('[data-rs-num-min]');
  var nMax = root.querySelector('[data-rs-num-max]');
  var fill = root.querySelector('[data-rs-fill]');

  /* ── El estado manda, no los <input type=range> ──────────────────────────
     Antes `lo()`/`hi()` leían el valor de los controles deslizantes, y el
     navegador redondea ese valor al `step`. Resultado: escribir 1.000.000
     filtraba por 999.600. Ahora el valor exacto vive aquí; los deslizantes son
     solo la superficie para arrastrar y se sincronizan desde el estado. */
  var state = {
    lo: Math.max(min, Math.min(vMin, max)),
    hi: Math.max(min, Math.min(vMax, max))
  };

  function lo() { return state.lo; }
  function hi() { return state.hi; }

  /* Los dos controles se superponen y cualquiera puede cruzar al otro, así que
     el menor de los dos es siempre el mínimo. */
  function readFromSliders() {
    var a = Number(iMin.value);
    var b = Number(iMax.value);
    state.lo = Math.min(a, b);
    state.hi = Math.max(a, b);
  }

  function writeToSliders() {
    iMin.value = state.lo;
    iMax.value = state.hi;
  }

  function paint() {
    if (document.activeElement !== nMin) nMin.value = fmt(lo());
    if (document.activeElement !== nMax) nMax.value = fmt(hi());
    var p1 = (lo() - min) / (max - min) * 100;
    var p2 = (hi() - min) / (max - min) * 100;
    fill.style.left = p1 + '%';
    fill.style.width = (p2 - p1) + '%';
    /* Cuando los dos thumbs se acercan, el input de arriba tapa al de abajo y
       en móvil deja de agarrarse. Subimos el z-index del thumb mínimo al
       solaparse para que siempre se pueda tomar (el máximo se alcanza por el
       passthrough de pointer-events). */
    var overlap = (p2 - p1) < 12;
    iMin.style.zIndex = overlap ? '5' : '3';
    iMax.style.zIndex = '4';
  }

  function commit() {
    if (opts.onCommit) opts.onCommit(lo(), hi());
  }

  [iMin, iMax].forEach(function(i) {
    i.addEventListener('input', function() { readFromSliders(); paint(); });
    i.addEventListener('change', function() { readFromSliders(); paint(); commit(); });
  });

  /* Ingreso manual: acepta dígitos (con puntos de miles), clampea al rango y
     mueve el thumb correspondiente. Aplica al salir del campo o con Enter. */
  function parseNum(str) {
    var n = parseInt(String(str).replace(/[^\d]/g, ''), 10);
    return isNaN(n) ? null : n;
  }
  function applyNum(input, isMin) {
    /* opts.parse convierte el texto MOSTRADO (formateado por fmt) de vuelta al
       dominio del slider. Sin él, el round-trip rompe cuando fmt no es 1:1 (ej.
       money: fmt divide /100 → el valor se encogía 100× y caía a 0). */
    var n = opts.parse ? opts.parse(input.value) : parseNum(input.value);
    if (n === null || isNaN(n)) { paint(); return; }
    n = Math.max(min, Math.min(n, max));
    /* El valor escrito entra al estado tal cual. Los deslizantes se colocan
       después: si el navegador los redondea, se mueve el pulgar unas décimas
       de píxel, pero lo que se filtra es lo que se escribió. */
    if (isMin) { state.lo = Math.min(n, state.hi); }
    else { state.hi = Math.max(n, state.lo); }
    writeToSliders();
    paint();
    commit();
  }
  nMin.addEventListener('change', function() { applyNum(nMin, true); });
  nMax.addEventListener('change', function() { applyNum(nMax, false); });
  [nMin, nMax].forEach(function(inp) {
    inp.addEventListener('keydown', function(e) { if (e.key === 'Enter') { e.preventDefault(); inp.blur(); } });
  });

  paint();
  return { values: function() { return [lo(), hi()]; } };
};

/* Curación de resultados de búsqueda.
 *
 * Las reglas NO viven en el tema: viven en Typesense como `presets` (uno por
 * término curado, `cur-<término>`), así que cualquier cliente que consulte el
 * índice —este tema, una app, otro sitio— obtiene el mismo orden sin duplicar
 * nada. Acá solo se arma el nombre del preset.
 *
 * Un preset inexistente lo ignora el servidor en silencio, así que se puede
 * mandar siempre: si hay curación para el término, aplica; si no, el orden es
 * el normal por relevancia.
 *
 * Precedencia (verificada contra el servidor): un `sort_by` explícito en la
 * consulta gana sobre el del preset. Es lo que se quiere — si el cliente elige
 * "precio: menor a mayor", la curación se aparta.
 *
 * Nota: los presets se pierden si se recrea la colección; van junto con los
 * sinónimos en el procedimiento de reindexado.
 */
ThemeUtils.curationPreset = function(query) {
  if (!query) return null;
  var q = String(query).trim().toLowerCase();
  if (!q) return null;
  /* Un término, sin caracteres raros: los presets se llaman cur-<término>.
     Las consultas de varias palabras no se curan hoy. */
  if (!/^[a-záéíóúñü0-9]+$/.test(q)) return null;
  return 'cur-' + q;
};
